/**
 * Parses errors returned by the tax-closing backend (NestJS HttpException
 * payloads) into a typed, actionable error description.
 *
 * Backend may return:
 *   - 403 month gate:        { statusCode: 403, message: "...", status: "NOT_FOUND" | "BLOCKED" | "CLOSED", month, year }
 *   - 403 store permission:  { statusCode: 403, message: "...", unauthorizedStores: [1, 7] }
 *   - 409 active job:        { statusCode: 409, message: "..." }
 *   - generic:               { statusCode: number, message: string }
 */

export type TaxClosingErrorKind =
  | "month-not-released"
  | "month-not-found"
  | "month-closed"
  | "stores-unauthorized"
  | "active-job-conflict"
  | "validation"
  | "unknown";

export interface ParsedTaxClosingError {
  kind: TaxClosingErrorKind;
  title: string;
  description: string;
  /** Optional metadata that callers can use to render CTAs */
  meta: {
    month?: number;
    year?: number;
    unauthorizedStores?: number[];
  };
}

interface RawErrorPayload {
  statusCode?: number;
  status?: string;
  message?: string | string[];
  month?: number;
  year?: number;
  unauthorizedStores?: number[];
}

interface RtkQueryError {
  status?: number | string;
  data?: RawErrorPayload | string;
  message?: string;
}

const TITLE_BY_KIND: Record<TaxClosingErrorKind, string> = {
  "month-not-released": "Mês não liberado",
  "month-not-found": "Mês não cadastrado",
  "month-closed": "Mês encerrado",
  "stores-unauthorized": "Sem permissão",
  "active-job-conflict": "Já existe extração em andamento",
  validation: "Dados inválidos",
  unknown: "Erro ao criar extração",
};

const FALLBACK_DESCRIPTION_BY_KIND: Record<TaxClosingErrorKind, string> = {
  "month-not-released":
    "O mês selecionado precisa ser liberado pelo TI antes de receber extrações.",
  "month-not-found":
    "Esse mês ainda não foi cadastrado. Libere-o em Controle de Meses Fiscais.",
  "month-closed":
    "Esse período fiscal já foi encerrado e não aceita novas extrações.",
  "stores-unauthorized":
    "Você não tem permissão para extrair dados de uma ou mais lojas selecionadas.",
  "active-job-conflict":
    "Aguarde a extração atual finalizar antes de iniciar uma nova.",
  validation: "Verifique os parâmetros enviados.",
  unknown: "Erro desconhecido ao criar extração.",
};

const KIND_BY_MONTH_GATE_STATUS: Record<string, TaxClosingErrorKind> = {
  NOT_FOUND: "month-not-found",
  BLOCKED: "month-not-released",
  CLOSED: "month-closed",
};

function extractPayload(err: unknown): RawErrorPayload {
  const rtkErr = err as RtkQueryError | undefined;
  if (!rtkErr) return {};
  if (typeof rtkErr.data === "string") {
    return { message: rtkErr.data, statusCode: Number(rtkErr.status) || undefined };
  }
  return {
    statusCode: Number(rtkErr.status) || rtkErr.data?.statusCode,
    ...(rtkErr.data ?? {}),
  };
}

function flattenMessage(message: string | string[] | undefined): string {
  if (!message) return "";
  return Array.isArray(message) ? message.join(", ") : message;
}

function classifyError(payload: RawErrorPayload): TaxClosingErrorKind {
  // 403 store permission
  if (payload.unauthorizedStores && payload.unauthorizedStores.length > 0) {
    return "stores-unauthorized";
  }
  // 403 month gate (status field comes from MonthGateService)
  if (payload.statusCode === 403 && payload.status) {
    return KIND_BY_MONTH_GATE_STATUS[payload.status] ?? "month-not-released";
  }
  // 409 conflict
  if (payload.statusCode === 409) {
    return "active-job-conflict";
  }
  // 400 validation
  if (payload.statusCode === 400) {
    return "validation";
  }
  return "unknown";
}

export function parseTaxClosingError(err: unknown): ParsedTaxClosingError {
  const payload = extractPayload(err);
  const kind = classifyError(payload);

  const message = flattenMessage(payload.message);
  const description = message || FALLBACK_DESCRIPTION_BY_KIND[kind];

  return {
    kind,
    title: TITLE_BY_KIND[kind],
    description,
    meta: {
      month: payload.month,
      year: payload.year,
      unauthorizedStores: payload.unauthorizedStores,
    },
  };
}
